import {Typography} from '@hconnect/uikit'
import DeleteIcon from '@mui/icons-material/Delete'
import {Box, IconButton, TextField} from '@mui/material'
import {Controller, useFormContext} from 'react-hook-form'

import {useTranslation} from '../../../../../hooks/useTranslation'
import {
  CustomerTier,
  CustomerTierListItemProps,
  CustomerTierSettings
} from '../types/CustomerTierSettings'

import {useStyles} from './CustomerTierListItem.styles'

export const CustomerTierListItem = ({index, onDelete, totalTiers}: CustomerTierListItemProps) => {
  const {t} = useTranslation()
  const {control, watch, trigger} = useFormContext<CustomerTierSettings>()
  const {classes} = useStyles()
  const allTiers = watch('tiers') || []

  const validateAllFields = () => {
    allTiers.forEach((_, tierIndex) => {
      void trigger(`tiers.${tierIndex}.name`)
      void trigger(`tiers.${tierIndex}.leadTimeInBusinessDays`)
    })
  }

  const handleDelete = () => {
    if (totalTiers > 1) {
      onDelete(index)
    }
  }
  const getFieldName = <K extends keyof CustomerTier>(fieldName: K): `tiers.${number}.${K}` => {
    return `tiers.${index}.${fieldName}`
  }

  const isLastRemainingTier = totalTiers === 1

  return (
    <Box sx={{display: 'flex', gap: 2, alignItems: 'center', mb: 2}}>
      <Box sx={{flex: 1}}>
        <Controller
          name={getFieldName('name')}
          control={control}
          rules={{
            required: t(
              'configurations.hconnect.customerTierSettings.configurationDetails.requiredField',
              {
                fieldName: t(
                  'configurations.hconnect.customerTierSettings.configurationDetails.tierNameLabel'
                )
              }
            ),
            minLength: {
              value: 1,
              message: 'Tier name must be at least 1 character'
            },
            maxLength: {
              value: 20,
              message: 'Tier name must be no more than 20 characters'
            },
            validate: {
              isUnique: (currentNameValue) => {
                const currentTier = allTiers[index]
                // Normalize undefined to null for consistent comparison
                const currentLeadTime = currentTier?.leadTimeInBusinessDays ?? null

                const isDuplicate = allTiers.some((otherTier, otherIndex) => {
                  // Normalize undefined to null for consistent comparison
                  const otherLeadTime = otherTier.leadTimeInBusinessDays ?? null

                  return (
                    otherIndex !== index &&
                    otherTier.name === currentNameValue &&
                    otherLeadTime === currentLeadTime
                  )
                })

                return (
                  !isDuplicate ||
                  t(
                    'configurations.hconnect.customerTierSettings.configurationDetails.tierNameAndLeadTimeNotUnique'
                  )
                )
              }
            }
          }}
          render={({field: controllerField, fieldState}) => (
            <Box>
              <TextField
                {...controllerField}
                fullWidth
                label={t(
                  'configurations.hconnect.customerTierSettings.configurationDetails.tierNameLabel'
                )}
                type="text"
                variant="standard"
                className={`${classes.textField} ${fieldState.error ? classes.errorField : ''}`}
                InputProps={{disableUnderline: true}}
                InputLabelProps={{className: classes.inputLabel, shrink: true}}
                error={!!fieldState.error}
                data-test-id={`customer-tier-name-input-${index}`}
                onChange={(e) => {
                  controllerField.onChange(e)
                  // When name changes, revalidate all fields for all tiers
                  void trigger(getFieldName('leadTimeInBusinessDays'))
                  validateAllFields()
                }}
              />
              {fieldState.error && (
                <Typography
                  className={classes.errorText}
                  data-test-id={`customer-tier-settings-validation-${getFieldName(
                    'name'
                  )}-${index}`}
                >
                  {fieldState.error.message}
                </Typography>
              )}
            </Box>
          )}
        />
      </Box>
      <Box sx={{flex: 1}}>
        <Controller
          name={getFieldName('percentage')}
          control={control}
          rules={{
            required: t(
              'configurations.hconnect.customerTierSettings.configurationDetails.requiredField',
              {
                fieldName: t(
                  'configurations.hconnect.customerTierSettings.configurationDetails.tierPercentageLabel'
                )
              }
            ),
            min: {
              value: 1,
              message: t(
                'configurations.hconnect.customerTierSettings.configurationDetails.tierPercentageNotValid'
              )
            },
            max: {
              value: 99,
              message: t(
                'configurations.hconnect.customerTierSettings.configurationDetails.tierPercentageNotValid'
              )
            }
          }}
          render={({field: controllerField, fieldState}) => (
            <Box>
              <TextField
                {...controllerField}
                fullWidth
                label={t(
                  'configurations.hconnect.customerTierSettings.configurationDetails.tierPercentageLabel'
                )}
                type="number"
                variant="standard"
                className={`${classes.textField} ${fieldState.error ? classes.errorField : ''}`}
                InputProps={{disableUnderline: true}}
                InputLabelProps={{className: classes.inputLabel, shrink: true}}
                error={!!fieldState.error}
                data-test-id={`customer-tier-percentage-input-${index}`}
              />
              {fieldState.error && (
                <Typography
                  className={classes.errorText}
                  data-test-id={`customer-tier-settings-validation-${getFieldName(
                    'percentage'
                  )}-${index}`}
                >
                  {fieldState.error.message}
                </Typography>
              )}
            </Box>
          )}
        />
      </Box>
      <Box sx={{flex: 1}}>
        <Controller
          name={getFieldName('leadTimeInBusinessDays')}
          control={control}
          rules={{
            min: {
              value: 0,
              message: t(
                'configurations.hconnect.customerTierSettings.configurationDetails.leadTimeNotValid'
              )
            },
            validate: {
              isUnique: (currentLeadTimeValue) => {
                const currentTier = allTiers[index]
                const currentName = currentTier?.name
                // Normalize undefined to null for consistent comparison
                const normalizedCurrentValue = currentLeadTimeValue ?? null

                const isDuplicate = allTiers.some((otherTier, otherIndex) => {
                  // Normalize undefined to null for consistent comparison
                  const otherLeadTime = otherTier.leadTimeInBusinessDays ?? null

                  return (
                    otherIndex !== index &&
                    otherTier.name === currentName &&
                    otherLeadTime === normalizedCurrentValue
                  )
                })

                return (
                  !isDuplicate ||
                  t(
                    'configurations.hconnect.customerTierSettings.configurationDetails.tierNameAndLeadTimeNotUnique'
                  )
                )
              }
            }
          }}
          render={({field: controllerField, fieldState}) => (
            <Box>
              <TextField
                {...controllerField}
                fullWidth
                label="Lead Time In Days"
                type="number"
                variant="standard"
                className={`${classes.textField} ${fieldState.error ? classes.errorField : ''}`}
                InputProps={{disableUnderline: true}}
                InputLabelProps={{className: classes.inputLabel, shrink: true}}
                error={!!fieldState.error}
                placeholder="Optional"
                value={controllerField.value || ''}
                onChange={(e) => {
                  const value = e.target.value
                  const newValue = value === '' ? null : parseInt(value, 10)
                  controllerField.onChange(newValue)
                  // When lead time changes, revalidate all fields for all tiers
                  void trigger(getFieldName('name'))
                  validateAllFields()
                }}
                data-test-id={`customer-tier-leadtime-input-${index}`}
              />
              {fieldState.error && (
                <Typography
                  className={classes.errorText}
                  data-test-id={`customer-tier-settings-validation-${getFieldName(
                    'leadTimeInBusinessDays'
                  )}-${index}`}
                >
                  {fieldState.error.message}
                </Typography>
              )}
            </Box>
          )}
        />
      </Box>
      <IconButton
        onClick={handleDelete}
        color="error"
        disabled={isLastRemainingTier}
        data-test-id={`customer-tier-delete-button-${index}`}
      >
        <DeleteIcon />
      </IconButton>
    </Box>
  )
}
